import React, { createContext, useState, useCallback, useEffect } from 'react';

export interface AuthContextType {
  isAuthenticated: boolean;
  user: {
    email: string;
    name: string;
  } | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: async () => {
    throw new Error('AuthContext not initialized');
  },
  logout: () => {
    throw new Error('AuthContext not initialized');
  },
});

// Mock user data for testing
const MOCK_USERS = [
  { email: 'demo@edaxiq.com', password: 'password123', name: 'Demo User' },
  { email: 'admin@edaxiq.com', password: 'admin123', name: 'Admin User' },
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthContextType['user']>(null);

  // Check for existing auth token on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (token) {
        // For demo purposes, we'll just set a mock user
        setUser({ email: 'demo@edaxiq.com', name: 'Demo User' });
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if user exists in mock data
      const mockUser = MOCK_USERS.find(u => u.email === email && u.password === password);
      
      if (!mockUser) {
        throw new Error('Invalid credentials');
      }
      
      // Set user data
      setUser({ email: mockUser.email, name: mockUser.name });
      setIsAuthenticated(true);
      
      // Store token in appropriate storage
      const token = 'mock-jwt-token-' + Date.now();
      if (rememberMe) {
        localStorage.setItem('auth_token', token);
      } else {
        sessionStorage.setItem('auth_token', token);
      }
      
      console.log('Login successful:', { email, rememberMe });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    console.log('Logout successful');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}; 